import type { LabelTemplate } from "../lib/labels";
import type { LayoutMode, SortMode, TextStyle } from "../lib/types";

const PREFIX = "slidesci_for_mac:";

export interface AppSettings {
  titleFontName: string;
  titleFontSize: number;
  titleDistance: number;
  titleText: string;
  titleAlign: "left" | "center";
  labelFontName: string;
  labelFontSize: number;
  labelOffsetX: number;
  labelOffsetY: number;
  labelTemplate: LabelTemplate;
  labelBold: boolean;
  labelIndex: number;
  labelAutoUpdate: boolean;
  colNum: number;
  colSpace: number;
  rowSpace: number;
  imgWidth: string;
  imgHeight: string;
  layoutMode: LayoutMode;
  sortMode: SortMode;
  codeDarkBackground: boolean;
  codeLanguage: string;
  allowBlockEquationImageFallback: boolean;
  allowInlineEquationImageFallback: boolean;
}

type LegacySettings = Partial<AppSettings> & {
  allowEquationImageFallback?: boolean;
};

export const defaultSettings: AppSettings = {
  titleFontName: "微软雅黑",
  titleFontSize: 14,
  titleDistance: 0,
  titleText: "图片标题",
  titleAlign: "center",
  labelFontName: "Arial",
  labelFontSize: 12,
  labelOffsetX: -20,
  labelOffsetY: -7,
  labelTemplate: "A",
  labelBold: true,
  labelIndex: 1,
  labelAutoUpdate: true,
  colNum: 3,
  colSpace: 10,
  rowSpace: 25,
  imgWidth: "",
  imgHeight: "",
  layoutMode: "columnMaxWidth",
  sortMode: "position",
  codeDarkBackground: true,
  codeLanguage: "matlab",
  allowBlockEquationImageFallback: true,
  allowInlineEquationImageFallback: false,
};

export function loadSettings(): AppSettings {
  if (typeof localStorage === "undefined") {
    return defaultSettings;
  }

  const raw = localStorage.getItem(`${PREFIX}settings`);
  if (!raw) {
    return defaultSettings;
  }

  try {
    const parsed = JSON.parse(raw) as LegacySettings;
    const legacyFallback = typeof parsed.allowEquationImageFallback === "boolean" ? parsed.allowEquationImageFallback : undefined;
    return {
      ...defaultSettings,
      ...parsed,
      allowBlockEquationImageFallback: legacyFallback ?? parsed.allowBlockEquationImageFallback ?? defaultSettings.allowBlockEquationImageFallback,
      allowInlineEquationImageFallback: legacyFallback ?? parsed.allowInlineEquationImageFallback ?? defaultSettings.allowInlineEquationImageFallback,
    };
  } catch {
    return defaultSettings;
  }
}

export function saveSettings(settings: AppSettings): void {
  localStorage.setItem(`${PREFIX}settings`, JSON.stringify(settings));
}

export interface ClipboardState {
  width?: number;
  height?: number;
  centers?: Array<{ left: number; top: number }>;
  style?: {
    text?: TextStyle;
    fillColor?: string;
    borderColor?: string;
    borderWeight?: number;
  };
}

export function loadClipboardState(): ClipboardState {
  if (typeof localStorage === "undefined") {
    return {};
  }
  const raw = localStorage.getItem(`${PREFIX}clipboard`);
  if (!raw) {
    return {};
  }
  try {
    return JSON.parse(raw) as ClipboardState;
  } catch {
    return {};
  }
}

export function saveClipboardState(state: ClipboardState): void {
  localStorage.setItem(`${PREFIX}clipboard`, JSON.stringify(state));
}

export function loadLatexShapeMap(): Record<string, string> {
  if (typeof localStorage === "undefined") {
    return {};
  }
  const raw = localStorage.getItem(`${PREFIX}latex-shapes`);
  if (!raw) {
    return {};
  }
  try {
    return JSON.parse(raw) as Record<string, string>;
  } catch {
    return {};
  }
}

export function saveLatexForShape(shapeId: string, latex: string): void {
  const current = loadLatexShapeMap();
  current[shapeId] = latex;
  localStorage.setItem(`${PREFIX}latex-shapes`, JSON.stringify(current));
}

export function getLatexForShape(shapeId: string): string | undefined {
  return loadLatexShapeMap()[shapeId];
}

export interface LatexMetadataSource {
  shapeId?: string;
  shapeName?: string;
  tagLatex?: string;
  altTextDescription?: string;
}

export function resolveLatexSource(source: LatexMetadataSource, localMap: Record<string, string> = loadLatexShapeMap()): string | undefined {
  const tagged = source.tagLatex?.trim();
  if (tagged) {
    return tagged;
  }
  const altText = source.altTextDescription?.trim();
  if (altText) {
    return altText;
  }
  if (source.shapeName && localMap[source.shapeName]) {
    return localMap[source.shapeName];
  }
  return source.shapeId ? localMap[source.shapeId] : undefined;
}
